(function () {
  // Scroll-overlay layout: each banner is a pinned 100vh panel; the later one
  // rises over the previous (CSS sticky stacking = the bottom-to-top mask
  // reveal). This script keeps each panel's image pinned to the viewport as
  // the panel rises, leaving only a small residual drift for parallax.
  const PARALLAX = 0.95; // small residual drift only (1 = image rides the panel)
  const DESKTOP_BP = 576;
  const FOCUS_CLEARANCE = 16;
  const FOCUS_SETTLE = 120;

  class MediaBannerScroll {
    constructor(el) {
      this.el = el;
      this.panels = Array.from(el.querySelectorAll(".media-banner__wrapper"));
      this.media = this.panels.map((p) =>
        p.querySelector(".media-banner__media-container")
      );
      this.ticking = false;
      this._onScroll = this._onScroll.bind(this);
      this._update = this._update.bind(this);
      this._onFocusIn = this._onFocusIn.bind(this);
      this._checkFocus = this._checkFocus.bind(this);

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onScroll, { passive: true });
      el.addEventListener("focusin", this._onFocusIn);
      this._update();
    }

    destroy() {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onScroll);
      this.el.removeEventListener("focusin", this._onFocusIn);
      clearTimeout(this.focusTimer);
      this.focusTarget = null;
      this.media.forEach((m) => {
        if (m) m.style.removeProperty("--media-banner-parallax");
      });
    }

    _onFocusIn(event) {
      const target = event.target;
      const index = this.panels.findIndex((panel) => panel.contains(target));
      if (index === -1 || index === this.panels.length - 1) return;

      this.focusTarget = target;
      this.focusPanelIndex = index;
      this._scheduleFocusCheck();
    }

    _scheduleFocusCheck() {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(this._checkFocus, FOCUS_SETTLE);
    }

    _checkFocus() {
      const target = this.focusTarget;
      this.focusTarget = null;
      if (!target || document.activeElement !== target) return;
      if (window.innerWidth < DESKTOP_BP) return;

      const next = this.panels[this.focusPanelIndex + 1];
      if (!next) return;

      for (let step = 0; step < 4; step++) {
        const overlap =
          target.getBoundingClientRect().bottom +
          FOCUS_CLEARANCE -
          next.getBoundingClientRect().top;
        if (overlap <= 0) return;

        const scrollTop = window.scrollY;
        window.scrollBy({ top: -overlap, behavior: "instant" });
        if (window.scrollY === scrollTop) return;
      }
    }

    _onScroll() {
      if (this.focusTarget) this._scheduleFocusCheck();
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this._update);
    }

    _update() {
      this.ticking = false;
      const active = window.innerWidth >= DESKTOP_BP;
      const vh = window.innerHeight || document.documentElement.clientHeight;

      this.panels.forEach((panel, i) => {
        const media = this.media[i];
        if (!media) return;

        if (!active) {
          media.style.removeProperty("--media-banner-parallax");
          return;
        }

        // Panel top runs from the viewport height (just below the fold) to 0
        // as it rises into its pinned position. Counter-translate the image by
        // that amount so it appears pinned to the viewport while the panel's
        // clipping edge reveals it bottom-to-top; PARALLAX leaves a small drift.
        // Never positive: the overscan sits below the panel (top-anchored scale
        // in the scss), so a downward shift would uncover the top edge.
        const top = panel.getBoundingClientRect().top;
        const ty = -Math.max(0, Math.min(top, vh)) * (1 - PARALLAX);
        media.style.setProperty(
          "--media-banner-parallax",
          ty.toFixed(2) + "px"
        );
      });
    }
  }

  // Ambient parallax for the scroll_overlay layout's static first panel
  // (panel 2 already drifts via MediaBannerScroll above). Independent of it —
  // this just gives the static panel underneath a comparable sense of depth.
  // Share of the viewport height the media may drift. The container is scaled by
  // $media-banner-overscan (scss) from its top edge, and that overscan is what
  // the drift eats into — raise one and the other has to follow.
  const PARALLAX_MAX_SHIFT_RATIO = 0.05;

  class MediaBannerParallax {
    constructor(el, wrappers, options) {
      this.el = el;
      this.wrappers =
        wrappers || Array.from(el.querySelectorAll(".media-banner__wrapper"));
      this.media = this.wrappers.map((w) =>
        w.querySelector(".media-banner__media-container")
      );
      // The scroll_overlay's first panel is position:sticky (pinned in
      // place) while everything else scrolls past it, which reads as the
      // opposite motion relationship compared to a normal panel scrolling
      // by — flip the sign there so it still reads as "lagging" the scroll.
      this.sign = options && options.invert ? -1 : 1;
      this.desktop = window.matchMedia(`(min-width: ${DESKTOP_BP}px)`);
      this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.ticking = false;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._update = this._update.bind(this);

      window.addEventListener("scroll", this._onScroll, { passive: true });
      window.addEventListener("resize", this._onResize, { passive: true });
      this._measure();
      this._update();
    }

    destroy() {
      window.removeEventListener("scroll", this._onScroll);
      window.removeEventListener("resize", this._onResize);
      this._clear();
    }

    _clear() {
      this.media.forEach((m) => {
        if (m) m.style.removeProperty("--media-banner-parallax-shift");
      });
    }

    // getBoundingClientRect().top freezes at 0 for a position:sticky panel
    // while it's pinned (that's the whole point of sticky), so it can't
    // drive a scroll-progress value for scroll_overlay's first banner.
    // offsetTop walked up the offsetParent chain instead gives each
    // wrapper's normal-flow document position, which sticky doesn't affect,
    // so it keeps changing with scrollY the whole time the panel is stuck.
    _measure() {
      this.rootHeight = this.el.offsetHeight;
      this.docTops = this.wrappers.map((wrapper) => {
        let top = 0;
        let node = wrapper;
        while (node) {
          top += node.offsetTop;
          node = node.offsetParent;
        }
        return top;
      });
      this.heights = this.wrappers.map((wrapper) => wrapper.offsetHeight);
    }

    _onResize() {
      this._measure();
      this._onScroll();
    }

    _onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(this._update);
    }

    _update() {
      this.ticking = false;

      if (!this.desktop.matches || this.reduceMotion.matches) {
        this._clear();
        return;
      }

      const vh = window.innerHeight || document.documentElement.clientHeight;
      const scrollY = window.scrollY || window.pageYOffset;

      this.wrappers.forEach((wrapper, i) => {
        const media = this.media[i];
        if (!media) return;

        // 0 while the panel is still below the viewport top, 1 once it has been
        // scrolled through its whole pinned range — so the media sits neutral
        // until the section is in place and only then starts to lag the scroll.
        const height = this.heights[i];
        const distance = Math.max(1, this.rootHeight - height);
        const progress = (scrollY - this.docTops[i]) / distance;
        const clamped = Math.max(0, Math.min(1, progress));
        const shift = (
          this.sign *
          clamped *
          vh *
          PARALLAX_MAX_SHIFT_RATIO
        ).toFixed(2);
        media.style.setProperty("--media-banner-parallax-shift", shift + "px");
      });
    }
  }

  function initSection(section) {
    if (!section || !section.classList.contains("media-banner-section")) return;

    const scrollRoot = section.querySelector(".media-banner--scroll");
    if (scrollRoot) {
      if (scrollRoot._mediaBannerScroll) {
        scrollRoot._mediaBannerScroll.destroy();
        scrollRoot._mediaBannerScroll = null;
      }
      scrollRoot._mediaBannerScroll = new MediaBannerScroll(scrollRoot);

      // Panel 1 sits static under panel 2 the whole time (see the scss
      // comment above), so give it its own ambient parallax instead of
      // leaving it motionless.
      const firstWrapper = scrollRoot.querySelector(".media-banner__wrapper");
      if (firstWrapper) {
        if (scrollRoot._mediaBannerParallax) {
          scrollRoot._mediaBannerParallax.destroy();
          scrollRoot._mediaBannerParallax = null;
        }
        scrollRoot._mediaBannerParallax = new MediaBannerParallax(
          scrollRoot,
          [firstWrapper],
          { invert: true }
        );
      }
    }
  }

  initSection(document.currentScript.parentElement);

  if (!window.__themeMediaBannerScrollEvents) {
    window.__themeMediaBannerScrollEvents = true;

    document.addEventListener("shopify:section:load", function (event) {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", function (event) {
      event.target.querySelectorAll(".media-banner--scroll").forEach((el) => {
        if (!el._mediaBannerScroll) return;
        el._mediaBannerScroll.destroy();
        el._mediaBannerScroll = null;
      });
      event.target.querySelectorAll(".media-banner").forEach((el) => {
        if (!el._mediaBannerParallax) return;
        el._mediaBannerParallax.destroy();
        el._mediaBannerParallax = null;
      });
    });
  }
})();
